/**
 * Auth-related click handlers — open-login, logout, delete-account-request.
 *
 * Each handler is a side-effect function called by the dispatcher in
 * `actions/index.ts` after matching `[data-action]`. Handlers decide
 * themselves whether to `e.preventDefault()`.
 *
 * Originally inline in `main.ts` lines 107-110, 245-268. Phase 2.3 split 2026-05-12.
 */
import { Auth } from '../auth';
import { t } from '../i18n';
import { showToast } from '../toast';
import { openAuth } from '../modals';
import { haptic as tgHaptic, tgConfirm } from '../telegram-mini-app';

export function openLoginAction(e: MouseEvent): void {
  e.preventDefault();
  tgHaptic.tap();
  openAuth();
}

export function logoutAction(e: MouseEvent): void {
  e.preventDefault();
  tgHaptic.tapLight();
  void Auth.logout();
}

/**
 * Two-step confirmation per operator request 2026-05-06, then a RECORDED
 * deletion request.
 *
 * This used to end in `window.location.href = 'mailto:...'`, which does nothing
 * at all for a visitor without a configured mail client — so the right the
 * privacy policy promises could fail silently, leaving no trace anywhere
 * (audit 2026-07-29). It now POSTs to /api/me/deletion-request, which files a
 * support ticket and raises an operator event.
 *
 * Erasure stays operator-mediated on purpose: a live eSIM has to be released
 * and a remaining balance settled first, and payment records are retained for
 * as long as accounting law requires. The wording in both confirmations says
 * exactly that instead of promising instant destruction.
 *
 * In a Telegram Mini App `tgConfirm()` renders TG's native confirm sheet; on
 * web it falls back to `window.confirm`.
 */
export function deleteAccountRequestAction(e: MouseEvent, _target: HTMLElement): void {
  e.preventDefault();
  tgHaptic.warning();
  void (async (): Promise<void> => {
    if (!(await tgConfirm(t('dash.settings.deleteConfirm1')))) return;
    if (!(await tgConfirm(t('dash.settings.deleteConfirm2')))) return;
    try {
      const res = await fetch('/api/me/deletion-request', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error(String(res.status));
      showToast(t('dash.settings.deleteRequested'), 'success');
    } catch {
      showToast(t('dash.settings.deleteFailed'), 'error');
    }
  })();
}
